import { useEffect, useRef } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { toast } from 'sonner'

import { normalizeRole } from '@/features/auth/permissions'
import { useSession } from '@/features/auth/use-session'
import type { Role } from '@/shared/types/domain'

import type { ReactNode } from 'react'

interface GuardProps {
  children?: ReactNode
}

/**
 * `<RequireAuth>` NO renderiza nada en estado `loading`.
 *
 * Si renderizara un spinner o redirigiera, cada recarga con sesion valida
 * mostraria un flash de `/login` mientras Firebase rehidrata y `/me` viaja.
 * Devolver `null` es deliberado.
 */
export function RequireAuth({ children }: GuardProps) {
  const { status } = useSession()
  const location = useLocation()

  if (status === 'loading') return null

  if (status === 'anonymous') {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />
  }

  return <>{children ?? <Outlet />}</>
}

export interface RequireRoleProps extends GuardProps {
  allow: readonly Role[]
  /** A donde mandar a un interno sin permiso. Default `/403`. */
  fallbackPath?: string
}

/**
 * Guard de rol con DOS fallbacks distintos, y la diferencia es de seguridad:
 *
 * - **Cliente en una URL interna** -> redirect silencioso a `/portal` con un
 *   toast neutro. NO mostramos `/403`: un 403 explicito confirma que el recurso
 *   existe, y eso ya es informacion. El layout interno nunca se monta y la
 *   query nunca se dispara.
 * - **Interno sin rol admin** -> `/403` con mensaje claro. Aca no hay fuga: ya
 *   sabe que el sistema tiene configuracion, solo no le toca.
 */
export function RequireRole({ allow, children, fallbackPath = '/403' }: RequireRoleProps) {
  const { status, me } = useSession()
  const role = normalizeRole(me?.role)
  const allowed = role !== null && allow.includes(role)
  const isClientOut = role === 'client' && !allowed

  // El toast va en un efecto: avisar durante el render duplicaria el mensaje en
  // StrictMode y ensuciaria el arbol.
  const notified = useRef(false)
  useEffect(() => {
    if (isClientOut && !notified.current) {
      notified.current = true
      toast('Esta sección no está disponible en tu portal.')
    }
  }, [isClientOut])

  if (status === 'loading') return null
  if (status === 'anonymous') return <Navigate to="/login" replace />

  if (isClientOut) return <Navigate to="/portal" replace />
  if (!allowed) return <Navigate to={fallbackPath} replace />

  return <>{children ?? <Outlet />}</>
}

/** Manda a cada rol a su landing. Se usa en `/`. */
export function RoleHomeRedirect() {
  const { status, me } = useSession()

  if (status === 'loading') return null
  if (status === 'anonymous') return <Navigate to="/login" replace />

  return <Navigate to={normalizeRole(me?.role) === 'client' ? '/portal' : '/portfolio'} replace />
}

/** Si ya hay sesion, `/login` no tiene sentido. */
export function RedirectIfAuthenticated({ children }: GuardProps) {
  const { status, me } = useSession()

  if (status === 'loading') return null
  if (status === 'authenticated') {
    return <Navigate to={normalizeRole(me?.role) === 'client' ? '/portal' : '/portfolio'} replace />
  }

  return <>{children ?? <Outlet />}</>
}
